import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Building2, Microscope } from "lucide-react";
import axios from "axios";
import { StockTicker } from "@/components/StockTicker";
import { ArticlePlaceholder } from "@/components/ArticlePlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Investment ticker data
const tickers = [
  { symbol: "LEXX", name: "Lexaria Bioscience" },
  { symbol: "LOBEF", name: "Lobe Sciences" },
  { symbol: "ROMJF", name: "Rubicon Organics" },
  { symbol: "MDLH", name: "MD Logic Health®" },
  { symbol: "HLNO", name: "HealthNoise" },
  { symbol: "PPMT", name: "PPMT Strategic" },
  { symbol: "OLAR", name: "Olaregen Therapeutix" },
  { symbol: "BLUV", name: "Blu Vitals" },
];

export default function HomePage() {
  const [news, setNews] = useState([]);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news?limit=3`);
        setNews(response.data);
      } catch (error) {
        console.error("Failed to fetch news:", error);
      }
    };
    fetchNews();
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-background"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761437856554-3072a8143bc3?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        />
        <div className="hero-overlay" />

        <div className="relative z-10 min-h-screen flex flex-col justify-center px-6 md:px-12 lg:px-24 pt-20">
          <div className="max-w-4xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-6"
            >
              Strategic Investment Partners
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-7xl text-white mb-8 leading-[1.1]"
            >
              Unlocking Value in
              <span className="block gold-gradient-text">Tomorrow's Leaders</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-slate-400 text-base md:text-lg font-light leading-relaxed max-w-2xl mb-12"
            >
              Unleashed Ventures is a strategic investment firm specializing in micro-cap 
              companies and private entity acquisitions. We identify exceptional opportunities 
              in healthcare, biotechnology, and emerging technologies.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/portfolio"
                data-testid="hero-view-portfolio-btn"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-8 py-4 transition-all duration-300 btn-gold-glow"
              >
                View Portfolio
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-slate-700 text-white hover:border-amber-400/50 hover:text-amber-400 uppercase tracking-widest text-xs px-8 py-4 backdrop-blur-sm transition-all duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-amber-400/50 to-transparent" />
        </motion.div>
      </section>

      {/* Ticker Marquee */}
      <section className="py-6 border-y border-white/5 bg-[#0F172A]/50">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...tickers, ...tickers].map((ticker, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-3 px-8 border-r border-white/10"
              >
                <span className="text-amber-400 font-bold text-sm">{ticker.symbol}</span>
                <span className="text-slate-500 text-xs">{ticker.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live Stock Ticker */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#0F172A]/30" data-testid="live-stock-section">
        <div className="max-w-7xl mx-auto">
          <StockTicker />
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="thesis-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
                Our Approach
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-8">
                Strategic Value Creation
              </h2>
              <p className="text-slate-400 font-light leading-relaxed mb-8">
                We focus on identifying and investing in micro-cap companies with exceptional 
                growth potential. Our deep industry expertise and hands-on approach enables us 
                to unlock value that others overlook.
              </p>
              <div className="space-y-6">
                {[
                  {
                    icon: Microscope,
                    title: "Biotech & Healthcare",
                    desc: "Pioneering investments in next-generation therapeutics",
                  },
                  {
                    icon: Building2,
                    title: "Private Acquisitions",
                    desc: "Strategic acquisition of high-potential private entities",
                  },
                  {
                    icon: TrendingUp,
                    title: "Micro-Cap Focus",
                    desc: "Early identification of undervalued growth opportunities",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">{item.title}</h4>
                      <p className="text-slate-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1769751641224-dbbda144e824?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Modern architecture"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-[#0F172A] p-8 border border-white/10">
                <p className="text-amber-400 text-4xl font-bold mb-2">8+</p>
                <p className="text-slate-400 text-sm uppercase tracking-wider">Portfolio Companies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Portfolio */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F172A]/30" data-testid="featured-portfolio">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
                Investments
              </p>
              <h2 className="text-3xl sm:text-4xl text-white">Featured Portfolio</h2>
            </div>
            <Link
              to="/portfolio"
              data-testid="view-all-portfolio-link"
              className="mt-4 md:mt-0 text-slate-400 hover:text-amber-400 transition-colors text-sm flex items-center gap-2"
            >
              View All Investments
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "Lexaria Bioscience",
                ticker: "LEXX",
                category: "Biotech",
                logo: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/wr8e7cqa_lexaria%20bioscience.png",
              },
              {
                name: "Rubicon Organics",
                ticker: "ROMJF",
                category: "Agriculture",
                logo: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/f60wexhw_rubicon%20organics.png",
              },
              {
                name: "PPMT Strategic Group",
                ticker: "PPMT",
                category: "Strategic",
                logo: "https://customer-assets.emergentagent.com/job_9b5c43cb-efbb-4d52-9a1f-74b736257153/artifacts/d1gxmb7o_ppmt%20strategic%20group.png",
              },
            ].map((company, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="portfolio-card group bg-slate-900/50 border border-white/5 hover:border-amber-400/30 transition-all duration-500"
              >
                <div className="aspect-[16/10] overflow-hidden bg-white flex items-center justify-center">
                  <img
                    src={company.logo}
                    alt={company.name}
                    className="w-full h-full object-contain p-4"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs px-2 py-1 badge-${company.category.toLowerCase()}`}>
                      {company.category}
                    </span>
                    {company.ticker && (
                      <span className="text-amber-400 text-xs font-medium">{company.ticker}</span>
                    )}
                  </div>
                  <h3 className="text-white text-lg font-medium group-hover:text-amber-400 transition-colors">
                    {company.name}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* News Preview */}
      {news.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="news-preview-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
              <div>
                <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
                  Insights
                </p>
                <h2 className="text-3xl sm:text-4xl text-white">Latest News</h2>
              </div>
              <Link
                to="/news"
                data-testid="view-all-news-link"
                className="mt-4 md:mt-0 text-slate-400 hover:text-amber-400 transition-colors text-sm flex items-center gap-2"
              >
                View All News
                <ArrowRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((article, index) => (
                <Link
                  key={article.id}
                  to="/news"
                  state={{ selectedArticleId: article.id }}
                  data-testid={`news-card-link-${article.id}`}
                >
                  <motion.article
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden mb-4">
                      {article.image_url ? (
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <ArticlePlaceholder className={`w-full h-full ${article.image_url ? 'hidden' : ''}`} />
                    </div>
                    <span className="text-amber-400 text-xs uppercase tracking-wider">
                      {article.category}
                    </span>
                    <h3 className="text-white text-lg mt-2 mb-3 group-hover:text-amber-400 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-slate-500 text-sm line-clamp-2">{article.summary}</p>
                  </motion.article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F172A]/50" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-amber-400 uppercase tracking-[0.3em] text-xs mb-4">
            Partner With Us
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Ready to Explore Opportunities?
          </h2>
          <p className="text-slate-400 font-light mb-10 max-w-2xl mx-auto">
            Whether you're an investor seeking exceptional returns or a company looking 
            for strategic partnership, we'd like to hear from you.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="inline-flex items-center justify-center gap-2 bg-amber-500 text-[#08090D] hover:bg-amber-400 font-bold uppercase tracking-widest text-xs px-10 py-5 transition-all duration-300 btn-gold-glow"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
